import { useState, useRef } from 'react';
import {
  EmployeeDTO,
  useChangeStatusEmployee,
  useEmployees,
  useGetEmployeeRoleIsSeller,
} from '../../../hooks/useEmployee';
import TitlePage from '../../../components/TitlePage';
import { Column, TableComponent } from '../../../components/TableComponent';
import CreateUpdate from './components/createUpdate';
import { Input } from '@/components/ui/input';
import Delete from './components/delete';
import { Filter, Search } from 'lucide-react';
import { debounce } from 'lodash';
import LoadingCircle from '@/components/LoadingCircle';
import { Button } from '@/components/ui/button';
import {
  checkIsPermissionWithScreen,
  formatDate,
  renderUserStatus,
} from '@/lib/utils';
import { useProfileContext } from '@/hooks/useProfileContext';
import { Switch } from '@/components/ui/switch';
import { userStatus } from '@/api/employee';
import ChangeEmployee from './components/changeEmployee';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRoleContext } from '@/hooks/useRoleContext';

const limit = 10;
const Employee = () => {
  const { roles } = useRoleContext();
  const { profile } = useProfileContext();
  const searchRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const { data, isLoading, isFetching } = useEmployees({
    page,
    limit,
    search,
    status: status === '' ? undefined : (status as userStatus),
    roleKey: role === '' ? undefined : (role as string),
  });

  const { mutateAsync: changeStatusEmployee } = useChangeStatusEmployee();
  const { data: employeesData } = useGetEmployeeRoleIsSeller(false);

  const [openChangeEmployee, setOpenChangeEmployee] = useState(false);
  const [itemChangeEmployee, setItemChangeEmployee] =
    useState<EmployeeDTO | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingCircle />
      </div>
    );
  }

  const handleChangeStatus = (item: EmployeeDTO) => {
    if (item.customerCount > 0 && item.status === userStatus.ACTIVE) {
      setOpenChangeEmployee(true);
      setItemChangeEmployee(item);
      return;
    }
    changeStatusEmployee({
      id: item.id,
      status:
        item.status === userStatus.ACTIVE
          ? userStatus.INACTIVE
          : userStatus.ACTIVE,
    });
  };

  const columns: Column<EmployeeDTO>[] = [
    {
      header: 'Mã NV',
      cell: (item) => {
        return <div>NV-{item.id}</div>;
      },
    },
    {
      header: 'Tên nhân viên',
      accessorKey: 'username',
      cell: (item) => {
        return (
          <div className="font-medium text-luxury-900">{item.username}</div>
        );
      },
    },
    {
      header: 'Email',
      accessorKey: 'email',
    },
    {
      header: 'Trạng thái',
      accessorKey: 'status',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={item.status === userStatus.ACTIVE}
              onCheckedChange={() => {
                handleChangeStatus(item);
              }}
              disabled={item?.role?.key === 'admin'}
            />

            <span className={renderUserStatus(item.status).className}>
              {renderUserStatus(item.status).label}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Vị trí',
      accessorKey: 'role',
      cell: (item) => {
        return <div className="font-medium">{item.role.name}</div>;
      },
    },
    {
      header: 'Số điện thoại',
      accessorKey: 'phone',
    },
    {
      header: 'Địa chỉ',
      accessorKey: 'address',
      cell: (item) => {
        return (
          <div className="w-[300px] whitespace-pre-line break-words">
            {item.address}
          </div>
        );
      },
    },
    {
      header: 'Ngày sinh',
      accessorKey: 'dateOfBirth',
      cell: (item) => {
        return <div>{formatDate(item.dateOfBirth)}</div>;
      },
    },
    {
      header: 'Hành động',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            {checkIsPermissionWithScreen(
              'users',
              'update',
              profile?.role?.permissions
            ) && <CreateUpdate employee={item} />}
            {checkIsPermissionWithScreen(
              'users',
              'delete',
              profile?.role?.permissions
            ) &&
              item?.role?.key !== 'admin' && (
                <Delete
                  employee={item}
                  setPage={() => {
                    if (data?.employees?.length === 1 && page === data?.pages) {
                      setPage(page - 1);
                    }
                  }}
                />
              )}
          </div>
        );
      },
    },
  ];

  const debouncedSearch = debounce((value: string) => {
    setPage(1);
    setSearch(value);
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  return (
    <div className="luxury-theme min-h-screen">
      <ChangeEmployee
        itemChangeEmployee={itemChangeEmployee}
        employeesData={employeesData || []}
        open={openChangeEmployee}
        setOpen={setOpenChangeEmployee}
        setItemChangeEmployee={setItemChangeEmployee}
      />
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-2xl  text-luxury-900">
            <TitlePage />
          </div>
        </div>
        {checkIsPermissionWithScreen(
          'users',
          'create',
          profile?.role?.permissions
        ) && <CreateUpdate />}
      </div>

      <div className="flex items-center justify-end bg-white p-4 border-b border-luxury-200 shadow-sm gap-4">
        <div>
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value);
            }}
          >
            <SelectTrigger className="w-[150px] luxury-input">
              <SelectValue placeholder="Trạng thái" />
            </SelectTrigger>
            <SelectContent className="border-luxury-200">
              <SelectItem value={userStatus.ACTIVE}>Hoạt động</SelectItem>
              <SelectItem value={userStatus.INACTIVE}>
                Không hoạt động
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Select
            value={role}
            onValueChange={(value) => {
              setRole(value);
            }}
          >
            <SelectTrigger className="w-[150px] luxury-input">
              <SelectValue placeholder="Chọn vị trí" />
            </SelectTrigger>
            <SelectContent className="border-luxury-200">
              {roles?.map((role) => (
                <SelectItem key={role.id} value={role.key}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Input
            className="w-[300px] pr-10 luxury-input"
            placeholder="Tìm kiếm theo tên hoặc email"
            onChange={handleSearch}
            ref={searchRef}
          />
          <Search className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-luxury-500" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="luxury-button-outline"
            onClick={() => {
              setSearch('');
              if (searchRef.current) {
                searchRef.current.value = '';
              }
              setStatus('');
              setRole('');
            }}
          >
            <Filter className="w-4 h-4 mr-2" />
            Xoá lọc
          </Button>
        </div>
      </div>

      <div className="px-6 py-6 relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <TableComponent
          data={data?.employees || []}
          columns={columns}
          pageSize={limit}
          totalPages={data?.pages || 0}
          onPageChange={setPage}
          currentPage={page}
          className="luxury-table"
        />
      </div>
    </div>
  );
};

export default Employee;
